import { CommonModule, formatDate } from "@angular/common";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { DialogService } from "primeng/dynamicdialog";
import { InputMaskModule } from "primeng/inputmask";
import { Subscription, combineLatest } from "rxjs";
import { ButtonDirective } from "src/app/core/directives";
import {
    AutocompleteComponent,
    ConfirmDialogComponent,
    DateTimeComponent,
    RadioButtonComponent,
    TextInputComponent,
} from "src/app/shared/components/ui";

import { UppercaseDirective } from "src/app/shared/directive";
import {
    AcademicYearInterface,
    GenderOptions,
    PostChildrenInfoInterface,
    StandardInterface,
} from "src/app/shared/interfaces";

import { Component, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

import {
    admissionActions,
    appEventActions,
    lovsActions,
    selectActiveAcademicYearStandards,
    selectAdmissionData,
    selectAdmissionIdsData,
    selectData,
    selectIsMutationLoading,
    standardActions,
} from "src/app/shared/store";

import { BACKEND_DATE_FORMAT, BLOOD_GROUPS } from "src/app/shared/constants";
import { AdmissionService } from "src/app/features/public/admission/service";

import { CasteInterface, ReligionInterface, MotherTongueInterface } from "src/app/shared/interfaces/settings/lovs";

@Component({
    selector: "sms-children-info",
    standalone: true,
    imports: [
        CommonModule,
        TextInputComponent,
        RadioButtonComponent,
        DateTimeComponent,
        AutocompleteComponent,
        ReactiveFormsModule,
        ButtonDirective,
        InputMaskModule,
        UppercaseDirective,
    ],
    templateUrl: "./children-info.component.html",
    styleUrls: ["./children-info.component.scss"],
})
export class ChildrenInfoComponent implements OnInit, OnChanges, OnDestroy {
    @Input() preview = false;
    formGroup!: FormGroup;
    today = new Date();

    casteDataKey = "/castes/";
    casteData: CasteInterface[];
    religionDataKey = "/religion/";
    religionData: ReligionInterface[];

    motherTongueDataKey = "/mother-tongue/";
    aadharHelpText = "Please Enter a valid aadhar number.";
    motherTongueData: MotherTongueInterface[];
    genderOptions = Object.values(GenderOptions);
    academicYearDataKey = "/academic-year/";
    academicYears: AcademicYearInterface[];
    standards$ = this.store.select(selectActiveAcademicYearStandards);
    isLoading$ = this.store.select(selectIsMutationLoading);
    bloodGroupOptions = BLOOD_GROUPS;

    gender = [
        {
            label: "Male",
            value: "Male",
        },
        {
            label: "Female",
            value: "Female",
        },
    ];

    subscription: Subscription = new Subscription();
    constructor(
        private formBuilder: FormBuilder,
        private store: Store,
        private admissionService: AdmissionService,
        private dialogService: DialogService,
        private router: Router,
        @Inject(LOCALE_ID) private localeId: string,
    ) {
        this.formGroup = this.formBuilder.group({
            academic_year: new FormControl({ value: "", disabled: true }, []),
            standard: new FormControl("", Validators.required),
            batch: new FormControl("", Validators.required),
            first_name: new FormControl("", Validators.required),
            aadhaar_number: new FormControl("", Validators.required),
            middle_name: new FormControl("", Validators.required),
            last_name: new FormControl("", Validators.required),
            name_as_aadhar: new FormControl("", Validators.required),
            dob: new FormControl("", Validators.required),
            place_of_birth: new FormControl("", Validators.required),
            nationality: new FormControl("", Validators.required),
            mother_tongue: new FormControl("", Validators.required),
            gender: new FormControl("", Validators.required),
            religion: new FormControl("", Validators.required),
            caste: new FormControl("", Validators.required),
            sub_caste: new FormControl("", Validators.required),
            blood_group: new FormControl("", Validators.required),
            transport_facility_required: new FormControl(null, Validators.required),
            admission: new FormControl(null),
            student: new FormControl(null),
            phone: new FormControl("", Validators.required),
            is_update: false,
        });
    }

    ngOnInit(): void {
        scrollTo(0, 0);
        this.getSelectData();
        this.subscription.add(
            this.store
                .select(selectAdmissionIdsData)
                .subscribe((value) => value && this.formGroup.patchValue({ ...value })),
        );
        this.getStepData();
        // this.subscription.add(
        //     this.formGroup.get("academic_year")?.valueChanges.subscribe((value) => {
        //         if (value) {
        //             this.store.dispatch(standardActions.loadStandardsForAdmission({ academic_year: value.id }));
        //         }
        //         this.formGroup.get("standard")!.setValue("");
        //     }),
        // );

        this.subscription.add(
            this.formGroup.get("standard")!.valueChanges.subscribe((value) => {
                // if (value) {
                //     if (value.is_aadhaar_required) {
                //         this.formGroup.get("aadhaar_number")?.setValidators([Validators.required]);
                //     } else {
                //         this.formGroup.get("aadhaar_number")?.setValidators([]);
                //     }
                // }
                this.formGroup.get("batch")!.setValue("");
            }),
        );
        this.subscription.add(
            this.formGroup.get("caste")!.valueChanges.subscribe((value) => {
                this.formGroup.get("sub_caste")!.setValue("");
            }),
        );

        this.subscription.add(
            combineLatest([
                this.formGroup.get("phone")!.valueChanges,
                this.formGroup.get("dob")!.valueChanges,
            ]).subscribe(([phone, dob]) => {
                this.checkStudentExist(phone, dob);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["preview"].currentValue) {
            if (this.formGroup) {
                this.formGroup.disable();
            }
        }
    }

    convertDateFormat(ddmmyyyy: string) {
        // Assuming the input date is in "DD/MM/YYYY" format
        var parts = ddmmyyyy.split("/");

        // Rearrange the parts to "MM/DD/YYYY" format
        var mmddyyyy = parts[1] + "/" + parts[0] + "/" + parts[2];

        return mmddyyyy;
    }

    onSave() {
        if (this.preview) {
            this.store.dispatch(admissionActions.nextStep());
            return;
        }

        this.formGroup.markAllAsTouched();
        if (this.formGroup.valid) {
            let standard: StandardInterface = this.formGroup.getRawValue().standard;
            const start = new Date(this.convertDateFormat(standard.age_criteria_start_threshold));
            const end = new Date(this.convertDateFormat(standard.age_criteria_end_threshold));
            const date = new Date(this.formGroup.getRawValue().dob);

            if (!(date.getTime() >= start.getTime() && date.getTime() <= end.getTime())) {
                this.store.dispatch(
                    appEventActions.addToast({
                        severity: "error",
                        detail: `We regret to inform you that ${this.formGroup.getRawValue().first_name
                            } is not eligible for admission. Admission to this standard is open for students born between ${standard.age_criteria_start_threshold
                            } and ${standard.age_criteria_end_threshold} only.`,
                    }),
                );
                return;
            }
            const data: PostChildrenInfoInterface = {
                step: 1,
                standard: this.formGroup.getRawValue().standard.id,
                batch: this.formGroup.getRawValue().batch.id,
                student: this.formGroup.getRawValue().student,
                academic_year: this.formGroup.getRawValue().academic_year.id,
                admission: this.formGroup.getRawValue().admission,
                phone: this.formGroup.getRawValue().phone,
                is_update: this.formGroup.getRawValue().is_update,
                student_data: {
                    ...this.formGroup.getRawValue(),
                    caste: this.formGroup.getRawValue().caste.id,
                    sub_caste: this.formGroup.getRawValue().sub_caste.id,
                    dob: formatDate(this.formGroup.getRawValue().dob, BACKEND_DATE_FORMAT, this.localeId),
                },
            };

            this.store.dispatch(admissionActions.saveChildrenInfo({ data }));
        }
    }

    checkStudentExist(phone: string, dob: Date | string) {
        if (this.preview || this.formGroup.getRawValue().is_update) {
            return;
        }
        const parsedPhone = (phone || "").toString().replace(/\D/g, "");
        if (parsedPhone.length !== 10 || !dob) {
            return;
        }
        const formattedDob = formatDate(dob, BACKEND_DATE_FORMAT, this.localeId);
        this.subscription.add(
            this.admissionService
                .checkStudentExist({ phone: parsedPhone, dob: formattedDob })
                .subscribe({
                    next: (response) => {
                        if (!response?.form_number) {
                            return;
                        }
                        const ref = this.dialogService.open(ConfirmDialogComponent, {
                            width: "500px",
                            contentStyle: { overflow: "auto" },
                            header: "Existing Admission Form Found",
                            data: {
                                content:
                                    `
                                    <div class="text-sm text-gray-600 leading-6">
                                        <p class="font-semibold text-gray-800 mb-2">
                                            An admission form already exists for this student:
                                        </p>

                                        <ul class="list-disc pl-5">
                                        <li><b>Form Number:</b> ${response.form_number}</li>
                                        <li><b>Student Name:</b> ${response.student_name}</li>
                                        <li><b>Phone:</b> ${response.phone}</li>
                                        <li><b>Date of Birth:</b> ${response.dob}</li>
                                            <li><b>Standard:</b> ${response.standard_name}</li>
                                            <li><b>Academic Year:</b> ${response.academic_year}</li>
                                            <li><b>Status:</b> ${response.status}</li>
                                        </ul>

                                        <p class="mt-3 font-medium">
                                            Do you want to continue with this admission?
                                        </p>
                                    </div>
`
                                ,
                            },
                            baseZIndex: 10000,
                            maximizable: false,
                            dismissableMask: true,
                            styleClass: "responsive-dialog",
                        });
                        this.subscription.add(
                            ref.onClose.subscribe((confirmed) => {
                                if (!confirmed) {
                                    // this.formGroup.patchValue({phone: "",dob: null});
                                    return;
                                }
                                const target = response.is_submitted ? "/admission/my-admissions" : "/admission/partial-admissions";
                                this.router.navigate([target], {
                                    queryParams: { form_number: response.form_number, dob: formattedDob, },
                                });
                            }),
                        );
                    },
                    error: () => { },
                }),
        );
    }

    getStepData() {
        this.store.dispatch(admissionActions.getAdmissionData({ stepNumber: 1 }));
        this.subscription.add(
            this.store.select(selectAdmissionData).subscribe((value) => {
                if (value) {
                    this.formGroup.patchValue(
                        {
                            standard: value?.standard,
                            academic_year: value?.academic_year,
                            batch: value?.batch,
                            first_name: value?.student?.first_name,
                            aadhaar_number: value?.student?.aadhaar_number,
                            middle_name: value?.student?.middle_name,
                            last_name: value?.student?.last_name,
                            name_as_aadhar: value?.student?.name_as_aadhar,
                            dob: new Date(value?.student?.dob),
                            place_of_birth: value?.student?.place_of_birth,
                            nationality: value?.student?.nationality,
                            mother_tongue: value?.student?.mother_tongue,
                            gender: value?.student?.gender,
                            caste: value?.student?.caste,
                            phone: value?.phone,
                            sub_caste: value?.student?.sub_caste,
                            blood_group: value?.student?.blood_group,
                            transport_facility_required: value?.student?.transport_facility_required,
                            is_update: true,
                        },
                        {
                            emitEvent: false,
                        },
                    );
                }
            }),
        );
    }

    getSelectData() {
        this.store.dispatch(lovsActions.getLovData({ data: { url: this.religionDataKey } }));
        this.store.dispatch(lovsActions.getLovData({ data: { url: this.casteDataKey } }));
        this.store.dispatch(lovsActions.getLovData({ data: { url: this.motherTongueDataKey } }));
        this.store.dispatch(lovsActions.getLovData({ data: { url: this.academicYearDataKey } }));
        this.store.dispatch(standardActions.loadActiveAcademicYearStandards({ admission_enabled: true }));

        this.subscription.add(
            this.store.select(selectData).subscribe((data) => {
                this.religionData = data[this.religionDataKey];
                this.casteData = data[this.casteDataKey];
                this.motherTongueData = data[this.motherTongueDataKey];
                this.academicYears = data[this.academicYearDataKey];

                if (this.academicYears && this.academicYears.length > 0) {
                    const today = new Date();
                    let activeAcademicYear = this.academicYears.find(
                        (year) => year.admission_start_date && year.admission_end_date && new Date(year.admission_start_date) <= today && new Date(year.admission_end_date) >= today
                    );
                    if (activeAcademicYear) {
                        this.formGroup.get("academic_year")?.setValue(activeAcademicYear);
                    }
                }
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
